import { Component, OnDestroy, OnInit } from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, ValidationErrors, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { firstValueFrom, forkJoin, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import moment from 'moment';
import { MasterModel } from '../../../../master/models/master.model';
import { MasterService } from '../../../../master/services/master.service';
import { NotificationService } from '../../../../shared/services/notification.service';
import { FinancialIncomeService as FinancialIncomeV1Service } from '../../../../../modules/invoice/financialincome/financialincome.service';
import { ActivityService } from '../../../../../modules/user/activity/activity.service';
import { LoadingDialogService } from '../../../../../shared/ui/loading/loading-dialog.service';
import { ActualSelected, Utils } from '../../../../../shared/utils';
import { consts } from '../../../../../consts';
import { roundAmount } from '../../../../../shared/round-amount';

const DEFAULT_ENTRANCE_ACCOUNT = '7690000001';
const DEFAULT_CONCEPTS = ['INGRESO FINANCIERO', 'DIVIDENDOS'];

@Component({
  selector: 'app-financial-income-form',
  templateUrl: './financial-income-form.component.html',
  styleUrls: ['./financial-income-form.component.css'],
  standalone: false
})
export class FinancialIncomeFormV2Component implements OnInit, OnDestroy {
  form: UntypedFormGroup;
  uid: string | null = null;

  isLoading = true;
  isSaving = false;
  isProjectEnable = false;

  concepts: MasterModel[] = DEFAULT_CONCEPTS.map(concept => ({ id: concept, display: concept }));
  bankAccounts: MasterModel[] = [];
  accounts: MasterModel[] = [];
  projects: MasterModel[] = [];

  readonly fiscalYear = ActualSelected.yearSelected;

  private readonly destroy$ = new Subject<void>();

  constructor(
    private fb: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private masterService: MasterService,
    private financialIncomeService: FinancialIncomeV1Service,
    private activityService: ActivityService,
    private loading: LoadingDialogService,
    private notification: NotificationService,
  ) {
    this.form = this.fb.group({
      concept: [null, Validators.required],
      amountWithoutTax: [null, [Validators.required, Validators.min(0.01)]],
      date: [this.defaultDate(), [Validators.required, control => this.fiscalYearValidator(control)]],
      commissions: [0, Validators.min(0)],
      entranceAccount: [null, [Validators.required, control => this.entranceAccountValidator(control)]],
      bankAccountUid: [null, [Validators.required, control => this.optionValidator(control, this.bankAccounts)]],
      projectUid: [null, control => this.optionValidator(control, this.projects)],
    });
  }

  ngOnInit(): void {
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe(params => {
      this.uid = params['financialIncomeUid'] || null;
      this.load();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  get irpfRate(): number {
    return this.rateFor(this.amount);
  }

  get retention(): number {
    return -this.round(this.amount * this.irpfRate / 100);
  }

  private get amount(): number {
    const value = Number(this.form.get('amountWithoutTax').value);
    return isFinite(value) && value > 0 ? value : 0;
  }

  private async load(): Promise<void> {
    this.isLoading = true;
    try {
      const [lookups, activity, income] = await Promise.all([
        firstValueFrom(forkJoin({
          bankAccounts: this.masterService.getBankAccounts(),
          accounts: this.masterService.getAccountingModels(),
        })),
        this.activityService.loadData(ActualSelected.activitySelected),
        this.uid ? this.financialIncomeService.loadData(this.uid) : Promise.resolve(null),
      ]);

      this.bankAccounts = (lookups.bankAccounts || []).map(account => ({
        ...account,
        display: account.metadata && account.metadata.bank ? `${account.metadata.bank} — ${account.display}` : account.display,
      }));
      this.accounts = (lookups.accounts || [])
        .filter(account => this.accountNumber(account).startsWith(consts.ACCOUNT_FINANCIAL_INCOME))
        .sort((a, b) => this.accountNumber(a).localeCompare(this.accountNumber(b)));

      this.isProjectEnable = !!(activity && activity.isProject);
      if (this.isProjectEnable) {
        this.projects = await firstValueFrom(this.masterService.getProjects()) || [];
      }

      if (income) {
        this.applyIncome(income);
      } else {
        const account = this.accounts.find(a => this.accountNumber(a) === DEFAULT_ENTRANCE_ACCOUNT);
        this.form.patchValue({ entranceAccount: account || null }, { emitEvent: false });
      }

      this.isLoading = false;
    } catch {
      this.isLoading = false;
      this.notification.errorBottom(this.uid ? 'No se pudo cargar el ingreso financiero.' : 'Error cargando datos del formulario.');
      if (this.uid) this.router.navigate(['invoice/financialincome']);
    }
  }

  private applyIncome(income: any): void {
    const bank = this.bankAccounts.find(b => this.sameId(b.id, income.bankAccountUid));
    const project = this.projects.find(p => this.sameId(p.id, income.projectUid));

    let account = this.accounts.find(a => this.accountNumber(a) === this.accountNumber(income.entranceAccount));
    if (!account && income.entranceAccount) {
      account = { id: income.entranceAccount, display: income.entranceAccount, metadata: { number: this.accountNumber(income.entranceAccount) } };
      this.accounts = [account, ...this.accounts];
    }

    const concept = income.concept ? String(income.concept) : '';
    if (concept && !this.concepts.some(c => c.display === concept)) {
      this.concepts = [...this.concepts, { id: concept, display: concept }];
    }

    this.form.patchValue({
      concept: concept ? { id: concept, display: concept } : null,
      amountWithoutTax: income.amountWithoutTax,
      date: income.date ? new Date(income.date) : null,
      commissions: income.commissions || 0,
      entranceAccount: account || null,
      bankAccountUid: bank || null,
      projectUid: project || null,
    }, { emitEvent: false });
  }

  onCancel(): void {
    this.router.navigate(['invoice/financialincome']);
  }

  onSave(): void {
    if (this.isSaving) return;

    this.form.markAllAsTouched();
    if (this.form.invalid) {
      this.showValidationErrors();
      return;
    }

    const value = this.form.getRawValue();
    const amount = Number(value.amountWithoutTax);
    const dto: any = {
      concept: this.conceptText(value.concept),
      amountWithoutTax: amount,
      retention: this.retention,
      irpfImport: -this.rateFor(amount),
      commissions: value.commissions ? Number(value.commissions) : 0,
      date: moment(value.date).format('YYYY-MM-DD'),
      entranceAccount: this.accountNumber(value.entranceAccount),
      bankAccountUid: value.bankAccountUid.id,
      projectUid: value.projectUid && value.projectUid.id ? value.projectUid.id : null,
      activityUid: ActualSelected.activitySelected,
      fiscalYear: parseInt(ActualSelected.yearSelected, 10),
    };
    if (this.uid) dto.uid = this.uid;

    const request = this.uid
      ? this.financialIncomeService.editData(this.uid, dto)
      : this.financialIncomeService.addData(dto);

    this.isSaving = true;
    this.loading.run(this.uid ? 'Guardando ingreso financiero…' : 'Creando ingreso financiero…', request)
      .then(() => {
        this.notification.okBottom('Ingreso financiero guardado correctamente');
        this.router.navigate(['invoice/financialincome']);
      })
      .catch(error => {
        this.notification.errorBottom(error && error.error && error.error.message ? error.error.message : 'Error guardando el ingreso financiero.');
      })
      .finally(() => this.isSaving = false);
  }

  private showValidationErrors(): void {
    const date = this.form.get('date');
    const labels: { [key: string]: string } = {
      concept: 'Indica el concepto.',
      amountWithoutTax: 'Indica el importe bruto (mayor que 0).',
      date: date.hasError('fiscalYear') ? `La fecha debe pertenecer al ejercicio ${this.fiscalYear}.` : 'Indica la fecha.',
      commissions: 'Las comisiones no pueden ser negativas.',
      entranceAccount: this.form.get('entranceAccount').hasError('account')
        ? `La cuenta contable debe pertenecer al grupo ${consts.ACCOUNT_FINANCIAL_INCOME}.`
        : 'Selecciona la cuenta contable de ingreso.',
      bankAccountUid: this.bankAccounts.length
        ? 'Selecciona la cuenta bancaria.'
        : 'Selecciona la cuenta bancaria (puedes crearla desde Mi Cuenta).',
      projectUid: 'Selecciona un proyecto de la lista.',
    };

    const messages = Object.keys(labels)
      .filter(key => this.form.get(key).invalid)
      .map(key => '• ' + labels[key]);

    this.notification.errorBottom('Revisa los campos marcados en rojo:\n' + messages.join('\n'));

    setTimeout(() => {
      const firstInvalid = document.querySelector('app-financial-income-form .mat-form-field-invalid, app-financial-income-form .mat-mdc-form-field-invalid');
      if (firstInvalid) firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
    });
  }

  private optionValidator(control: AbstractControl, options: MasterModel[]): ValidationErrors | null {
    const value = control.value;
    if (!value) return null;
    const id = typeof value === 'object' ? value.id : value;
    return options.some(option => this.sameId(option.id, id)) ? null : { option: true };
  }

  private entranceAccountValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    return this.accountNumber(control.value).startsWith(consts.ACCOUNT_FINANCIAL_INCOME) ? null : { account: true };
  }

  private fiscalYearValidator(control: AbstractControl): ValidationErrors | null {
    if (!control.value) return null;
    return Utils.DateIsInFiscalYear(control.value) ? null : { fiscalYear: true };
  }

  private defaultDate(): Date | null {
    const today = new Date();
    return String(today.getFullYear()) === String(ActualSelected.yearSelected) ? today : null;
  }

  private rateFor(amount: number): number {
    if (!(amount > 0)) return 0;
    if (amount <= 6000) return 19;
    if (amount <= 50000) return 21;
    return 23;
  }

  private round(value: number): number {
    return roundAmount(value);
  }

  private conceptText(concept: any): string {
    if (!concept) return '';
    if (typeof concept === 'string') return concept.trim();
    return String(concept.display || concept.id || '').trim();
  }

  private sameId(a: any, b: any): boolean {
    return a != null && b != null && String(a).toLowerCase() === String(b).toLowerCase();
  }

  private accountNumber(account: any): string {
    if (!account) return '';
    if (typeof account === 'string') return account.split('-')[0].trim();
    if (account.metadata && account.metadata.number) return String(account.metadata.number);
    const display = String(account.display || account.id || '');
    return display.split(' - ')[0].trim();
  }
}
